/**
 * Custom hook for fetching and managing contact form data
 * Handles loading regions, centers, topics, and user details
 * @module hooks/useContactData
 */

import { useState, useEffect, useCallback } from 'react';
import type { DropdownOption } from '../../components/common/Dropdown';
import { fetchContactDetails, fetchContactLocations } from '../../services/contact/contactService';
import { transformToDropdownOptions } from '../../utils/dropdownTransformers';
import { ERROR_MESSAGES, LOAD_VALUE_TYPES } from '../../constants/contact.constants';
import type { ContactDetails } from '../../types/contact.types';

interface UseContactDataReturn {
  /** User contact details (NUID, name, email) */
  userDetails: ContactDetails | null;
  /** Available region options */
  regionOptions: DropdownOption[];
  /** Available center options (filtered by selected region) */
  centerOptions: DropdownOption[];
  /** Available topic options (filtered by selected center) */
  topicOptions: DropdownOption[];
  /** Loading state for initial data */
  isLoadingInitialData: boolean;
  /** Loading state for centers */
  isLoadingCenters: boolean;
  /** Loading state for topics */
  isLoadingTopics: boolean;
  /** Error message if data loading fails */
  dataError: string;
  /** Load centers for a specific region */
  loadCenters: (regionCode: string) => Promise<void>;
  /** Load topics for a specific center */
  loadTopics: (regionCode: string, centerCode: string) => Promise<void>;
  /** Reset center and topic options */
  resetCenterAndTopics: () => void;
  /** Reset topic options only */
  resetTopics: () => void;
}

/**
 * Custom hook to manage contact form data fetching and state
 * Provides user details, dropdown options, and loading states
 */
export const useContactData = (): UseContactDataReturn => {
  const [userDetails, setUserDetails] = useState<ContactDetails | null>(null);
  const [regionOptions, setRegionOptions] = useState<DropdownOption[]>([]);
  const [centerOptions, setCenterOptions] = useState<DropdownOption[]>([]);
  const [topicOptions, setTopicOptions] = useState<DropdownOption[]>([]);
  
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoadingCenters, setIsLoadingCenters] = useState(false);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [dataError, setDataError] = useState('');

  /**
   * Load initial data: user details and region options
   */
  const loadInitialData = useCallback(async () => {
    setIsLoadingInitialData(true);
    setDataError('');
    
    try {
      // Fetch user contact details
      const details = await fetchContactDetails();
      setUserDetails(details);

      // Fetch regions via the contact locations endpoint
      const regions = await fetchContactLocations(LOAD_VALUE_TYPES.REGION);
      const regionOpts = transformToDropdownOptions(regions ?? [], true);
      setRegionOptions(regionOpts);
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? ERROR_MESSAGES.LOAD_INITIAL_DATA_FAILED;
      setDataError(errorMessage);
      console.error('Error loading initial contact data:', error);
    } finally {
      setIsLoadingInitialData(false);
    }
  }, []);

  /**
   * Load centers for the selected region
   */
  const loadCenters = useCallback(async (regionCode: string) => {
    if (!regionCode) {
      setCenterOptions([]);
      return;
    }

    setIsLoadingCenters(true);
    setDataError('');
    
    try {
      const centers = await fetchContactLocations(LOAD_VALUE_TYPES.CENTER, regionCode);
      const centerOpts = transformToDropdownOptions(centers, true);
      setCenterOptions(centerOpts);
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? ERROR_MESSAGES.LOAD_CENTERS_FAILED;
      setDataError(errorMessage);
      console.error('Error loading centers:', error);
    } finally {
      setIsLoadingCenters(false);
    }
  }, []);

  /**
   * Load topics for the selected center
   */
  const loadTopics = useCallback(async (regionCode: string, centerCode: string) => {
    if (!regionCode || !centerCode) {
      setTopicOptions([]);
      return;
    }

    setIsLoadingTopics(true);
    setDataError('');
    
    try {
      const topics = await fetchContactLocations(
        LOAD_VALUE_TYPES.FEEDBACK_TYPE,
        regionCode,
        centerCode
      );
      const topicOpts = topics.map((topic) => ({
        id: topic.code,
        label: topic.name,
        value: topic.code,
      }));
      setTopicOptions(topicOpts);
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? ERROR_MESSAGES.LOAD_TOPICS_FAILED;
      setDataError(errorMessage);
      console.error('Error loading topics:', error);
    } finally {
      setIsLoadingTopics(false);
    }
  }, []);

  /**
   * Reset center and topic options (when region changes)
   */
  const resetCenterAndTopics = useCallback(() => {
    setCenterOptions([]);
    setTopicOptions([]);
  }, []);

  /**
   * Reset topic options only (when center changes)
   */
  const resetTopics = useCallback(() => {
    setTopicOptions([]);
  }, []);

  // Load initial data on mount
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return {
    userDetails,
    regionOptions,
    centerOptions,
    topicOptions,
    isLoadingInitialData,
    isLoadingCenters,
    isLoadingTopics,
    dataError,
    loadCenters,
    loadTopics,
    resetCenterAndTopics,
    resetTopics,
  };
};
